import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@wix/wix-i18n-config';
import MobilePrice from '../MobilePrice';
import { ClientOffering } from '../../../services/client-offering';
import classNames from 'classnames';
import { PRODUCT } from '../mobile-constant';

interface MobilePackagesProps {
  clientOffering: ClientOffering;
  selectedProduct: number;
  setSelectedProduct: (productIndex: number, buttonType: string) => void;
  isPinToTop?: boolean;
}

export const MobileProducts: React.FunctionComponent<MobilePackagesProps> = (
  props: MobilePackagesProps,
) => {
  const [presentPinProduct, setPresentPinProduct] = useState<boolean>(false);
  const [touchXStart, setTouchXStart] = useState<number | undefined>(undefined);
  const [touchXEnd, setTouchXEnd] = useState<number | undefined>(undefined);
  const { t } = useTranslation();

  const { default: s } = props.isPinToTop
    ? require('./MobilePinProducts.scss')
    : require('./MobileProducts.scss');

  const dataHoodPrefix = props.isPinToTop ? 'mobile-pin-' : 'mobile-';
  const isCurrentProduct = props.clientOffering.isCurrentProduct;
  const showMostPopular =
    isCurrentProduct.limited() || isCurrentProduct.basic();

  const { selectedProduct, setSelectedProduct } = props;
  const products = props.clientOffering.getProductList(t('package.free.name'));
  const priceCurrency = props.clientOffering.getCurrency();

  const [leftPin, setLeftPin] = useState<number>(0);
  const refOfPin = useRef(null);
  const pinElementSize = 2350;
  const windowSize = 980;
  const pinMinimumLeft = -pinElementSize + windowSize;

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const currentWidth = window.innerWidth;
      const scrollNeededToShow = currentWidth * (542 / 982);
      if (scrollNeededToShow > currentScrollY) {
        setPresentPinProduct(false);
      } else {
        setPresentPinProduct(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('touchend', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchend', handleScroll);
    };
  }, [presentPinProduct]);

  useEffect(() => {
    switch (props.selectedProduct) {
      case PRODUCT.UNLIMITED:
        setLeftPin(0);
        break;
      case PRODUCT.PRO:
        setLeftPin(Math.round(pinMinimumLeft / 3));
        break;
      case PRODUCT.BASIC:
        setLeftPin(Math.round((pinMinimumLeft / 3) * 2));
        break;
      default:
        setLeftPin(pinMinimumLeft);
        break;
    }
    setTouchXStart(undefined);
    setTouchXEnd(undefined);
  }, [props.selectedProduct, pinMinimumLeft]);

  const selectedClassName = (index: number) => ({
    [s.isPinToTop]: presentPinProduct,
    [s.isSelected]: index === selectedProduct,
    [s.isProductLimited]: index === PRODUCT.FREE,
    [s.isProductBasic]: index === PRODUCT.BASIC,
    [s.isProductPro]: index === PRODUCT.PRO,
    [s.isProductUnlimited]: index === PRODUCT.UNLIMITED,
  });

  const handleTouchStart = (touchStartEvent: any) => {
    setTouchXStart(Math.round(touchStartEvent.targetTouches[0].clientX));
  };
  const handleTouchMove = async (touchMoveEvent: any) => {
    await setTouchXEnd(Math.round(touchMoveEvent.targetTouches[0].clientX));
  };

  useEffect(() => {
    if (
      !props.isPinToTop ||
      touchXEnd === undefined ||
      touchXStart === undefined
    ) {
      return;
    }

    const touchXDiff = touchXEnd - touchXStart;
    const newLeftPin = leftPin + touchXDiff;
    if (newLeftPin > 0) {
      setLeftPin(0);
      return;
    } else if (newLeftPin < pinMinimumLeft) {
      setLeftPin(pinMinimumLeft);
      return;
    }
    setLeftPin(newLeftPin);
    setTouchXStart(touchXEnd);
    setTouchXEnd(undefined);
  }, [leftPin, pinMinimumLeft, touchXStart, touchXEnd, props.isPinToTop]);

  const handleTouchEnd = () => {
    if (
      props.isPinToTop ||
      touchXEnd === undefined ||
      touchXStart === undefined
    ) {
      return;
    }

    const tenPercentOfWidth = window.innerWidth * 0.1;
    const touchXDiff = touchXEnd - touchXStart;
    if (Math.abs(touchXDiff) >= tenPercentOfWidth) {
      const swipeRight = touchXDiff > 0;
      if (swipeRight) {
        if (props.selectedProduct > PRODUCT.UNLIMITED) {
          props.setSelectedProduct(props.selectedProduct - 1, 'swipe_reg');
        }
      } else {
        if (props.selectedProduct < PRODUCT.FREE) {
          props.setSelectedProduct(props.selectedProduct + 1, 'swipe_reg');
        }
      }
    }
    setTouchXStart(undefined);
    setTouchXEnd(undefined);
  };

  return (
    <div ref={refOfPin}>
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          style={{ left: props.isPinToTop ? leftPin : 'unset' }}
          className={classNames(s.strip, selectedClassName(selectedProduct))}
          data-hook={`${dataHoodPrefix}products`}
        >
          {products.map((product: any, index: number) => (
            <div
              key={index}
              onClick={() => {
                const buttonType = props.isPinToTop ? 'hover' : 'regular';
                setSelectedProduct(index, buttonType);
              }}
            >
              <div
                className={classNames(s.productBox, selectedClassName(index))}
                data-hook={`${dataHoodPrefix}product`}
              >
                <div
                  className={classNames(
                    s.productName,
                    selectedClassName(index),
                  )}
                  data-hook={`${dataHoodPrefix}productName`}
                >
                  {product.name}
                </div>
                <div
                  className={classNames(
                    s.productPrice,
                    selectedClassName(index),
                  )}
                  data-hook={`${dataHoodPrefix}productPrice`}
                >
                  {index < 3 ? (
                    <MobilePrice
                      currency={priceCurrency}
                      amount={product.price}
                    />
                  ) : (
                    <div className={s.productFree}>
                      {t('package.free.price')}
                    </div>
                  )}
                </div>
              </div>
              {index === 1 && showMostPopular && (
                <div className={s.mostPopularStrip}>
                  <div
                    className={s.mostPopular}
                    data-hook={`${dataHoodPrefix}most-popular`}
                  >
                    {t('package.mostPopular')}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MobileProducts;
